import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon, InputControl, TextField } from '@/components';
import { useResolveValue } from '@/hooks';
import { ValuesType } from '@/types';
import { REGEX, ResolveDate, convert12HoursFormat, formatTimeUnit, getInputBlockAnswer, is12Hour, parseTime } from '@/utils';
import { Flex, IconButton, Popover, PopoverBody, PopoverContent, PopoverTrigger, Text, Tooltip, useDisclosure, useNumberInput } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BlockLayout } from '../fragments/BlockLayout';
export const Time = ({ block: { answer, clear_label, id, key, layout, required }, defaultValue = '', errors = [], onChange }) => {
    var _a;
    const popOver = useDisclosure();
    const [resolver] = useResolveValue();
    const resolved = {
        clearLabel: resolver.resolveString(clear_label),
        label: resolver.resolveString(answer.label),
        required: resolver.resolveBoolean(required)
    };
    const isInvalid = errors.length > 0;
    const inputWrapperRef = useRef(null);
    const [popoverMarginTop, setPopoverMarginTop] = useState(50); // adjust the popover position based on the element height
    useEffect(() => {
        if (inputWrapperRef.current) {
            const height = inputWrapperRef.current.clientHeight;
            setPopoverMarginTop(height);
        }
    }, []);
    const localTime = useMemo(() => {
        if (!defaultValue) {
            return {
                hh: '',
                mm: '',
                period: '',
                time: ''
            };
        }
        const { hours, minutes } = parseTime(defaultValue);
        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        const localizedTime = new ResolveDate().formatDateLocalized(date).time;
        const [formattedTime, period = ''] = localizedTime.split(' ');
        const [hh, mm] = formattedTime.split(':');
        const time = `${hh}:${mm} ${period}`;
        return {
            hh,
            mm,
            period,
            time
        };
    }, [defaultValue]);
    const formatTime = (options) => {
        var _a, _b;
        const { hours, minutes } = parseTime(defaultValue);
        let time = '';
        const hour = (_a = options === null || options === void 0 ? void 0 : options.hour) !== null && _a !== void 0 ? _a : hours;
        const minute = (_b = options === null || options === void 0 ? void 0 : options.minute) !== null && _b !== void 0 ? _b : minutes;
        if (is12Hour) {
            const timePeriod = `${hour}:${minute} ${localTime.period}`;
            time = convert12HoursFormat(timePeriod);
        }
        else {
            time = `${formatTimeUnit(hour)}:${formatTimeUnit(minute)}`;
        }
        return time;
    };
    const updateAnswer = (time = '') => {
        const answerValue = Object.assign(Object.assign({}, answer), { label: resolved.label, value: time ? time : null });
        const newAnswer = getInputBlockAnswer(ValuesType.TIME, answerValue, key);
        const isValid = REGEX.TIME.test(time);
        if (isValid || !time) {
            onChange(newAnswer);
        }
    };
    const handleDeselect = (e) => {
        e.preventDefault();
        updateAnswer();
    };
    const togglePeriod = (e) => {
        e.preventDefault();
        const newPeriod = localTime.period === 'AM' ? 'PM' : 'AM';
        const time = `${localTime.hh}:${localTime.mm} ${newPeriod}`;
        const newValue = convert12HoursFormat(time);
        updateAnswer(newValue);
    };
    const handleTimeChange = (value, type) => {
        const newValue = formatTime({ [type]: value });
        updateAnswer(newValue);
    };
    const { getDecrementButtonProps: hourDecrement, getIncrementButtonProps: hourIncrement, getInputProps: hourProps } = useNumberInput({
        max: is12Hour ? 12 : 23,
        min: is12Hour ? 1 : 0,
        value: (_a = localTime.hh) !== null && _a !== void 0 ? _a : (is12Hour ? 12 : 0),
        onChange: (_, val) => handleTimeChange(val, 'hour')
    });
    const { getDecrementButtonProps: minuteDecrement, getIncrementButtonProps: minuteIncrement, getInputProps: minuteProps } = useNumberInput({
        max: 59,
        min: 0,
        value: localTime.mm || 0,
        onChange: (_, val) => handleTimeChange(val, 'minute')
    });
    const ChevronIcon = ({ direction }) => (_jsx(Icon, { color: 'white', name: `chevron-${direction}`, size: '5', type: 'solid' }));
    const setDefaultValues = () => {
        if (!defaultValue) {
            handleTimeChange(0, 'hour');
            handleTimeChange(0, 'minute');
        }
    };
    const handleFocus = () => {
        setDefaultValues();
        if (!popOver.isOpen) {
            popOver.onOpen();
        }
    };
    return (_jsx(BlockLayout, Object.assign({ layout: layout }, { children: _jsxs(Popover, Object.assign({ isOpen: popOver.isOpen, onClose: popOver.onClose, onOpen: popOver.onOpen }, { children: [_jsx("div", Object.assign({ ref: inputWrapperRef }, { children: _jsx(PopoverTrigger, { children: _jsx(InputControl, Object.assign({ "data-test": id, error: errors[0] || '', isInvalid: isInvalid, label: resolved.label, onFocus: handleFocus }, { children: _jsx(TextField, { "data-test": answer.id, isReadOnly: true, leftElement: _jsx(Icon, { name: 'clock' }), onClick: setDefaultValues, rightElement: defaultValue && (_jsx(Tooltip, Object.assign({ label: resolved.clearLabel, placement: 'left' }, { children: _jsx(Icon, { cursor: 'pointer', name: 'close-circle', onClick: handleDeselect }) }))), value: localTime.time }) })) }) })), _jsx(PopoverContent, Object.assign({ bg: 'white', mt: `${popoverMarginTop}px`, w: 'auto' }, { children: _jsxs(PopoverBody, Object.assign({ alignItems: 'center', display: 'flex', gap: '2px' }, { children: [_jsxs(Flex, Object.assign({ align: 'center', dir: 'ltr', gap: '2px' }, { children: [_jsxs(Flex, Object.assign({ align: 'center', direction: 'column', gap: '1' }, { children: [_jsx(IconButton, Object.assign({ "aria-label": 'Increase Hours', icon: _jsx(ChevronIcon, { direction: 'up' }), size: 'sm' }, hourIncrement())), _jsx(TextField, Object.assign({ maxW: '55px', textAlign: 'center' }, hourProps(), { "aria-label": 'Hours', isDisabled: is12Hour })), _jsx(IconButton, Object.assign({ "aria-label": 'Decrease Hours', icon: _jsx(ChevronIcon, { direction: 'down' }), size: 'sm' }, hourDecrement()))] })), _jsx(Text, { children: ":" }), _jsxs(Flex, Object.assign({ align: 'center', direction: 'column', gap: '1' }, { children: [_jsx(IconButton, Object.assign({ "aria-label": 'Increase Minutes', icon: _jsx(ChevronIcon, { direction: 'up' }), size: 'sm' }, minuteIncrement())), _jsx(TextField, Object.assign({ maxW: '55px', textAlign: 'center' }, minuteProps(), { "aria-label": 'Minutes' })), _jsx(IconButton, Object.assign({ "aria-label": 'Decrease Minutes', icon: _jsx(ChevronIcon, { direction: 'down' }), size: 'sm' }, minuteDecrement()))] }))] })), is12Hour && (_jsxs(Flex, Object.assign({ direction: 'column', gap: '1', mx: '2' }, { children: [_jsx(IconButton, { "aria-label": 'Toggle Period', icon: _jsx(ChevronIcon, { direction: 'up' }), onMouseDown: togglePeriod, size: 'sm' }), _jsx(Text, Object.assign({ alignItems: 'center', display: 'flex', h: '50px', justifyContent: 'center' }, { children: localTime.period })), _jsx(IconButton, { "aria-label": 'Toggle Period', icon: _jsx(ChevronIcon, { direction: 'down' }), onMouseDown: togglePeriod, size: 'sm' })] })))] })) }))] })) })));
};
